import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Spinner, JobIdentity, EmptyState } from "@/components/bits";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Wrench, AlertTriangle, Trash2, Ban, Pencil, Loader2, Plus, Clock, User as UserIcon, CheckCircle2, Eye, ExternalLink, FileText } from "lucide-react";
import PartPhotos from "@/components/PartPhotos";

const REASONS = ["Salah Upload", "Duplicate SPK", "Salah Nopol", "Salah No Claim", "Salah Dokumen", "OCR Salah", "Lainnya"];

function PartsTable({ parts, testPrefix = "part", job = {}, canUpload = false }) {
  if (!parts.length) return <EmptyState title="Tidak ada data pada kategori ini" icon={AlertTriangle} />;
  const meta = { nopol: job.nopol, claim_number: job.claim_number };
  return (
    <Card className="overflow-hidden">
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2">Part Number</th><th className="text-left px-4 py-2">Part Name</th>
              <th className="text-center px-3 py-2">Req</th><th className="text-center px-3 py-2">Recv</th>
              <th className="text-center px-3 py-2">Remain</th><th className="text-center px-3 py-2">Issued</th>
              <th className="text-center px-3 py-2">Installed</th><th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Foto (Awal / Akhir)</th>
            </tr>
          </thead>
          <tbody>
            {parts.map((p, i) => (
              <tr key={p.id} className="border-t border-gray-100" data-testid={`${testPrefix}-row-${i}`}>
                <td className="px-4 py-2 font-mono-data font-semibold">{p.part_number || "—"}</td>
                <td className="px-4 py-2">{p.part_name}</td>
                <td className="text-center px-3 py-2">{p.required_qty}</td>
                <td className="text-center px-3 py-2 font-semibold">{p.received_qty}</td>
                <td className="text-center px-3 py-2">{p.remaining_qty}</td>
                <td className="text-center px-3 py-2">{p.issued_qty}</td>
                <td className="text-center px-3 py-2">{p.installed_qty}</td>
                <td className="px-4 py-2"><StatusBadge status={p.over_received ? "OVER" : p.status} /></td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-3">
                    <PartPhotos partLineId={p.id} phase="INITIAL" meta={{ ...meta, part_name: p.part_name, part_number: p.part_number }} canUpload={canUpload} compact />
                    <PartPhotos partLineId={p.id} phase="FINAL" meta={{ ...meta, part_name: p.part_name, part_number: p.part_number }} canUpload={canUpload} compact />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="md:hidden divide-y divide-gray-100">
        {parts.map((p, i) => (
          <div key={p.id} className="p-4" data-testid={`${testPrefix}-card-${i}`}>
            <div className="flex justify-between items-start gap-2">
              <div>
                <div className="font-mono-data font-semibold text-gray-900">{p.part_number || "—"}</div>
                <div className="text-sm text-gray-600">{p.part_name}</div>
              </div>
              <StatusBadge status={p.over_received ? "OVER" : p.status} />
            </div>
            <div className="grid grid-cols-5 gap-1 mt-3 text-center text-xs">
              {[["Req", p.required_qty], ["Recv", p.received_qty], ["Remain", p.remaining_qty], ["Issued", p.issued_qty], ["Inst", p.installed_qty]].map(([l, v]) => (
                <div key={l}><div className="text-gray-400">{l}</div><div className="font-semibold text-gray-900">{v}</div></div>
              ))}
            </div>
            <div className="mt-3 flex flex-col gap-2">
              <PartPhotos partLineId={p.id} phase="INITIAL" meta={{ ...meta, part_name: p.part_name, part_number: p.part_number }} canUpload={canUpload} compact />
              <PartPhotos partLineId={p.id} phase="FINAL" meta={{ ...meta, part_name: p.part_name, part_number: p.part_number }} canUpload={canUpload} compact />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("required");
  const [delOpen, setDelOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [preview, setPreview] = useState(null);

  const fileType = (f) => {
    const e = (f || "").split(".").pop().toLowerCase();
    return e === "pdf" ? "pdf" : (["jpg", "jpeg", "png", "webp"].includes(e) ? "image" : "other");
  };
  const openPreview = async (fname) => {
    if (!fname) { setPreview({ error: true }); return; }
    setPreview({ loading: true, type: fileType(fname) });
    try {
      const res = await api.get(`/files/${fname}`, { responseType: "blob" });
      setPreview({ url: URL.createObjectURL(res.data), type: fileType(fname) });
    } catch { setPreview({ error: true }); }
  };
  const closePreview = () => setPreview((p) => { if (p?.url) URL.revokeObjectURL(p.url); return null; });

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).finally(() => setLoading(false));
  }, [id]);
  useEffect(load, [load]);

  if (loading) return <Spinner />;
  if (!job) return <div className="py-16 text-center text-gray-500">Job tidak ditemukan</div>;

  const parts = job.required_parts || [];
  const total = parts.length;
  const complete = parts.filter((p) => p.received_qty >= p.required_qty).length;
  const remaining = parts.filter((p) => p.remaining_qty > 0);
  const isVoid = job.status === "VOID";
  const hasTxn = (job.receivings || []).length > 0 || (job.issues || []).length > 0;
  const canDelete = user.role === "owner" || (user.role === "partman" && (user.permissions || []).includes("delete_spk"));
  const canEdit = (user.role === "owner" || user.role === "partman") && !isVoid;

  return (
    <div>
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 mb-3" data-testid="back-btn">
        <ArrowLeft size={16} /> Kembali
      </button>

      {/* Sticky identity header — NOPOL big */}
      <Card className="p-5 mb-4 sticky top-16 z-10 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-400">NOPOL</div>
            <div className="nopol-lg font-bold text-gray-900 text-3xl sm:text-5xl" data-testid="detail-nopol">{job.nopol || "-"}</div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm">
              <span><span className="text-gray-400">No Claim:</span> <b className="font-mono-data">{job.claim_number || "—"}</b></span>
              <span><span className="text-gray-400">No SPK:</span> <b className="font-mono-data">{job.spk_number || "—"}</b></span>
              <span><span className="text-gray-400">Vehicle:</span> {job.vehicle_brand} {job.vehicle_type}</span>
              {job.customer_name && <span><span className="text-gray-400">Customer:</span> {job.customer_name}</span>}
              <span><span className="text-gray-400">Insurance:</span> {job.insurance || "—"}</span>
              {job.spk_date && <span><span className="text-gray-400">SPK Date:</span> {job.spk_date}</span>}
              <span><span className="text-gray-400">Job ID:</span> {job.job_number}</span>
            </div>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-2">
            <StatusBadge status={isVoid ? "PROBLEM" : job.status} testId="detail-status" />
            {isVoid && <span className="text-xs font-semibold text-red-600">VOID</span>}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => openPreview(job.spk_file)} data-testid="job-view-original-spk-btn" className="border-[rgba(0,200,255,0.45)] text-[var(--neon-cyan)]"><Eye size={14} className="mr-1" />View Original SPK</Button>
              {canEdit && <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-spk-btn"><Pencil size={14} className="mr-1" />Edit</Button>}
              {canDelete && !isVoid && !hasTxn && <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setDelOpen(true)} data-testid="delete-spk-btn"><Trash2 size={14} className="mr-1" />Delete SPK</Button>}
              {canDelete && !isVoid && hasTxn && <Button size="sm" variant="outline" className="text-amber-700 border-amber-200 hover:bg-amber-50" onClick={() => setVoidOpen(true)} data-testid="void-spk-btn"><Ban size={14} className="mr-1" />Void SPK</Button>}
              {!isVoid && !job.closed && (user.role === "owner" || user.role === "partman" || user.role === "warehouse_out") && (
                <Button size="sm" variant="outline" onClick={async () => {
                  try { const { data } = await api.post(`/jobs/${job.id}/close`); toast.success(`Job ditutup. Sisa part: ${data.remaining.length}`); load(); }
                  catch (e) { toast.error(apiError(e, "Gagal tutup job")); }
                }} data-testid="close-job-btn"><CheckCircle2 size={14} className="mr-1" />Tutup Job</Button>
              )}
              {job.closed && <span className="text-xs text-emerald-600 font-semibold">JOB CLOSED</span>}
            </div>
          </div>
        </div>
      </Card>

      {isVoid && (
        <Card className="p-3 mb-4 border-red-200 bg-red-50 text-sm text-red-700">
          <b>SPK VOID</b> — {job.void_reason}{job.void_notes ? ` (${job.void_notes})` : ""}. Histori & transaksi tetap tersimpan untuk audit.
        </Card>
      )}

      {job.over_received && (
        <Card className="p-3 mb-4 border-red-200 bg-red-50 flex items-center gap-2 text-red-700 text-sm font-medium">
          <AlertTriangle size={16} /> OVER RECEIVED — ada part melebihi Required Qty. Perlu konfirmasi supervisor.
        </Card>
      )}

      {!isVoid && (
        <Card className={`p-4 mb-4 ${job.status === "Complete" ? "bg-emerald-50 border-emerald-200" : job.status === "Partial" ? "bg-amber-50 border-amber-200" : "bg-gray-50"}`}>
          <div className="flex items-center justify-between">
            <div className="font-head font-semibold text-gray-900">
              {job.status === "Complete" ? "🟢 PART COMPLETE" : job.status === "Partial" ? "🟡 PARTIAL" : "⚪ WAITING"}
            </div>
            <div className="font-mono-data font-bold text-lg">{complete}/{total} Parts</div>
          </div>
          {remaining.length > 0 && (
            <div className="mt-2 text-sm text-gray-600">
              {remaining.length} part belum lengkap: {remaining.map((p) => p.part_name).slice(0, 4).join(", ")}{remaining.length > 4 ? "…" : ""}
            </div>
          )}
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto mb-4">
          <TabsTrigger value="required" data-testid="tab-required">Required</TabsTrigger>
          <TabsTrigger value="received" data-testid="tab-received">Received</TabsTrigger>
          <TabsTrigger value="remaining" data-testid="tab-remaining">Remaining</TabsTrigger>
          <TabsTrigger value="issued" data-testid="tab-issued">Issued</TabsTrigger>
          <TabsTrigger value="installed" data-testid="tab-installed">Installed</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
        </TabsList>
        <TabsContent value="required"><PartsTable parts={parts} testPrefix="req" job={job} canUpload={!isVoid} /></TabsContent>
        <TabsContent value="received"><PartsTable parts={parts.filter((p) => p.received_qty > 0)} testPrefix="recv" job={job} canUpload={!isVoid} /></TabsContent>
        <TabsContent value="remaining"><PartsTable parts={parts.filter((p) => p.remaining_qty > 0)} testPrefix="rem" job={job} canUpload={!isVoid} /></TabsContent>
        <TabsContent value="issued"><PartsTable parts={parts.filter((p) => p.issued_qty > 0)} testPrefix="iss" job={job} canUpload={!isVoid} /></TabsContent>
        <TabsContent value="installed"><PartsTable parts={parts.filter((p) => p.installed_qty > 0)} testPrefix="inst" job={job} canUpload={!isVoid} /></TabsContent>
        <TabsContent value="history">
          {(job.history || []).length === 0 ? <EmptyState title="Belum ada aktivitas" icon={Clock} /> : (
            <Card className="p-4">
              <div className="space-y-4">
                {job.history.map((h, i) => (
                  <div key={i} className="flex gap-3" data-testid={`history-${i}`}>
                    <div className="flex flex-col items-center">
                      <div className="h-2.5 w-2.5 rounded-full bg-[var(--gold)] mt-1.5" />
                      {i < job.history.length - 1 && <div className="w-px flex-1 bg-gray-200 my-1" />}
                    </div>
                    <div className="pb-1">
                      <div className="font-medium text-gray-900 text-sm">{h.action}</div>
                      {h.detail && <div className="text-sm text-gray-500">{h.detail}</div>}
                      <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-2">
                        <span className="flex items-center gap-1"><UserIcon size={11} />{h.user || "—"}{h.role && h.role !== "-" ? ` (${h.role})` : ""}</span>
                        {h.time && <span>{new Date(h.time).toLocaleString("id-ID")}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <DeleteVoidDialog
        open={delOpen} onOpenChange={setDelOpen} job={job} mode="delete"
        onDone={() => { toast.success("SPK dihapus"); navigate("/spk"); }}
        onNeedVoid={() => { setDelOpen(false); setVoidOpen(true); }}
      />
      <DeleteVoidDialog
        open={voidOpen} onOpenChange={setVoidOpen} job={job} mode="void"
        onDone={() => { toast.success("SPK di-VOID"); load(); }}
      />
      <EditDialog open={editOpen} onOpenChange={setEditOpen} job={job} hasTxn={hasTxn} onDone={() => { setEditOpen(false); toast.success("SPK diperbarui"); load(); }} />

      {/* Original SPK preview — available across the whole job lifecycle */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && closePreview()}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2"><FileText size={18} className="text-[var(--neon-cyan)]" />Original SPK</DialogTitle>
          </DialogHeader>
          {preview?.loading ? <Spinner /> : preview?.error ? (
            <div className="text-sm text-gray-400 py-10 text-center" data-testid="job-preview-unavailable">File original SPK tidak tersedia.</div>
          ) : preview?.url ? (
            <div className="flex-1 min-h-0" data-testid="job-spk-preview-content">
              {preview.type === "pdf" ? (
                <iframe title="SPK PDF" src={preview.url} className="w-full h-[70vh] rounded-lg border border-[rgba(0,200,255,0.25)] bg-white" />
              ) : preview.type === "image" ? (
                <div className="w-full h-[70vh] overflow-auto rounded-lg border border-[rgba(0,200,255,0.25)] flex items-center justify-center bg-black/40">
                  <img src={preview.url} alt="SPK" className="max-w-full max-h-full object-contain" />
                </div>
              ) : (
                <div className="text-sm text-gray-400 py-10 text-center">Format tidak didukung untuk preview.</div>
              )}
            </div>
          ) : null}
          <DialogFooter>
            {preview?.url && (
              <a href={preview.url} target="_blank" rel="noreferrer" data-testid="job-preview-new-tab">
                <Button variant="outline" className="border-[rgba(0,200,255,0.45)] text-[var(--neon-cyan)]"><ExternalLink size={16} className="mr-2" />Buka di tab baru</Button>
              </a>
            )}
            <Button variant="outline" onClick={closePreview} data-testid="job-preview-close-btn">Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DeleteVoidDialog({ open, onOpenChange, job, mode, onDone, onNeedVoid }) {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const isDelete = mode === "delete";

  const submit = async () => {
    if (!reason) return toast.error("Pilih alasan");
    if (reason === "Lainnya" && !notes.trim()) return toast.error("Isi catatan untuk 'Lainnya'");
    setSaving(true);
    try {
      if (isDelete) await api.delete(`/jobs/${job.id}`, { data: { reason, notes } });
      else await api.post(`/jobs/${job.id}/void`, { reason, notes });
      onDone();
    } catch (err) {
      if (err.response?.status === 409 && onNeedVoid) { toast.warning(apiError(err)); onNeedVoid(); }
      else toast.error(apiError(err, "Gagal"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-head">{isDelete ? "HAPUS SPK?" : "VOID / CANCEL SPK?"}</DialogTitle></DialogHeader>
        <div className="text-sm space-y-1 bg-gray-50 rounded-lg p-3">
          <div>Nopol: <b className="font-mono-data">{job.nopol}</b></div>
          <div>No Claim: <b className="font-mono-data">{job.claim_number || "—"}</b></div>
          <div>No SPK: <b className="font-mono-data">{job.spk_number || "—"}</b></div>
          <div>Required Parts: <b>{(job.required_parts || []).length}</b></div>
        </div>
        <p className={`text-sm ${isDelete ? "text-red-600" : "text-amber-700"}`}>
          {isDelete
            ? "SPK dan seluruh Required Parts yang belum memiliki transaksi akan dihapus. Tindakan ini tidak dapat dibatalkan."
            : "SPK akan ditandai VOID. Receiving/Issue/Installed & history tetap tersimpan untuk audit."}
        </p>
        <div>
          <label className="text-xs font-semibold uppercase text-gray-500">Alasan Penghapusan</label>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger className="mt-1" data-testid="reason-select"><SelectValue placeholder="Pilih alasan" /></SelectTrigger>
            <SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {reason === "Lainnya" && (
          <Textarea placeholder="Catatan (wajib)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="reason-notes" />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button onClick={submit} disabled={saving} className={isDelete ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700"} data-testid="confirm-delete-void-btn">
            {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}{isDelete ? "Confirm Delete" : "Confirm Void"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ open, onOpenChange, job, hasTxn, onDone }) {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setF({
      nopol: job.nopol || "", claim_number: job.claim_number || "", spk_number: job.spk_number || "",
      customer_name: job.customer_name || "", vehicle_brand: job.vehicle_brand || "",
      vehicle_type: job.vehicle_type || "", insurance: job.insurance || "", spk_date: job.spk_date || "",
      parts: (job.required_parts || []).map((p) => ({ part_number: p.part_number, part_name: p.part_name, qty: p.required_qty })),
    });
  }, [open, job]);

  if (!f) return null;
  const setP = (i, k, v) => { const parts = [...f.parts]; parts[i] = { ...parts[i], [k]: v }; setF({ ...f, parts }); };

  const submit = async () => {
    setSaving(true);
    try {
      const body = { ...f };
      if (hasTxn) delete body.parts; // parts cannot be edited after receiving
      await api.patch(`/jobs/${job.id}`, body);
      onDone();
    } catch (err) { toast.error(apiError(err, "Gagal edit")); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head">Edit SPK Data</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[["Nopol", "nopol"], ["No Claim", "claim_number"], ["No SPK", "spk_number"], ["Customer", "customer_name"], ["Merk", "vehicle_brand"], ["Type", "vehicle_type"], ["Insurance", "insurance"], ["SPK Date", "spk_date"]].map(([l, k]) => (
            <div key={k}>
              <label className="text-[11px] font-semibold uppercase text-gray-500">{l}</label>
              <Input className="mt-1 h-9" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`edit-${k}`} />
            </div>
          ))}
        </div>
        <div>
          <div className="text-xs font-semibold uppercase text-gray-500 mb-2">Required Parts</div>
          {hasTxn ? (
            <div className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3 flex items-center gap-2">
              <AlertTriangle size={16} /> Required Parts tidak dapat diubah karena sudah ada Receiving (perlu konfirmasi supervisor).
            </div>
          ) : (
            <div className="space-y-2">
              {f.parts.map((p, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <Input className="col-span-4 h-9 font-mono-data" placeholder="Part Number" value={p.part_number} onChange={(e) => setP(i, "part_number", e.target.value)} data-testid={`edit-part-num-${i}`} />
                  <Input className="col-span-6 h-9" placeholder="Nama Part" value={p.part_name} onChange={(e) => setP(i, "part_name", e.target.value)} />
                  <Input className="col-span-1 h-9" type="number" value={p.qty} onChange={(e) => setP(i, "qty", Number(e.target.value))} />
                  <button className="col-span-1 text-gray-400 hover:text-red-500" onClick={() => setF({ ...f, parts: f.parts.filter((_, x) => x !== i) })}><Trash2 size={16} /></button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => setF({ ...f, parts: [...f.parts, { part_number: "", part_name: "", qty: 1 }] })}><Plus size={14} className="mr-1" />Tambah Part</Button>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button onClick={submit} disabled={saving} className="bg-gray-900" data-testid="save-edit-btn">{saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
